import { useMemo, useState } from 'react'
import { format, startOfMonth, addMonths, subMonths, isSameDay } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { GestorEquipeFeriasCalendarAside } from '@/components/Gestores/GestorEquipeFeriasCalendarAside'
import {
  buildEventCountByDay,
  calendarDaysForMonth,
  dayEventMarkers,
  eventoDisplayMeta,
  FERIAS_DIA_MARKER_CLASS,
  formatPtDay,
  isSameMonthDay,
  isToday,
  visibleEventsOnDay,
} from '@/components/Gestores/gestorEquipeFeriasCalendarUtils'
import type { GestorEquipeFeriasCalendario } from '@/services/gestoresService'
import { cn } from '@/utils/cn'

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

type Props = {
  data: GestorEquipeFeriasCalendario
  loading?: boolean
  onSelectMember: (id: string) => void
}

export function GestorEquipeFeriasCalendar({ data, loading, onSelectMember }: Props) {
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()))
  const [selectedDay, setSelectedDay] = useState<Date | null>(() => new Date())

  const eventCountByDay = useMemo(
    () => buildEventCountByDay(data.eventos, viewMonth),
    [data.eventos, viewMonth],
  )
  const days = useMemo(() => calendarDaysForMonth(viewMonth), [viewMonth])
  const selectedEvents = useMemo(
    () => visibleEventsOnDay(data.eventos, selectedDay),
    [data.eventos, selectedDay],
  )

  if (loading) {
    return (
      <div className='glass-card flex min-h-[240px] items-center justify-center p-8'>
        <div className='h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent' />
      </div>
    )
  }

  return (
    <div className='grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]'>
      <div className='space-y-4'>
        <div className='glass-card overflow-hidden p-4 md:p-6'>
          <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
            <div>
              <p className='text-xs font-semibold uppercase tracking-wider text-text-muted'>
                Calendário de férias da equipe
              </p>
              <h2 className='text-lg font-bold capitalize text-text-primary md:text-xl'>
                {format(viewMonth, 'MMMM yyyy', { locale: ptBR })}
              </h2>
            </div>
            <div className='flex items-center gap-2'>
              <button
                type='button'
                aria-label='Mês anterior'
                onClick={() => setViewMonth(subMonths(viewMonth, 1))}
                className='rounded-xl border border-[var(--border)] p-2 text-text-muted hover:bg-white/[0.06]'
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type='button'
                aria-label='Próximo mês'
                onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                className='rounded-xl border border-[var(--border)] p-2 text-text-muted hover:bg-white/[0.06]'
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          <div className='mb-3 flex flex-wrap gap-3 text-[10px] text-text-muted'>
            <span className='inline-flex items-center gap-1.5'>
              <span className='h-2 w-2 rounded-full bg-violet-400' /> Programada
            </span>
            <span className='inline-flex items-center gap-1.5'>
              <span className='h-2 w-2 rounded-full bg-emerald-400' /> Em andamento
            </span>
            <span className='inline-flex items-center gap-1.5'>
              <span className='h-2 w-2 rounded-full bg-sky-400' /> Solicitação
            </span>
          </div>

          <div className='mb-2 grid grid-cols-7 gap-1'>
            {WEEKDAYS.map((label) => (
              <span key={label} className='py-2 text-center text-[11px] font-bold uppercase text-text-muted'>
                {label}
              </span>
            ))}
          </div>

          <div className='grid grid-cols-7 gap-1'>
            {days.map((day) => {
              const key = format(day, 'yyyy-MM-dd')
              const count = eventCountByDay.get(key) ?? 0
              const markers = dayEventMarkers(data.eventos, day)
              const outside = !isSameMonthDay(day, viewMonth)
              const selected = selectedDay ? isSameDay(day, selectedDay) : false
              const today = isToday(day)

              return (
                <button
                  key={key}
                  type='button'
                  onClick={() => setSelectedDay(day)}
                  className={cn(
                    'relative flex min-h-[56px] flex-col items-center justify-center rounded-xl border transition-all md:min-h-[68px]',
                    outside && 'border-transparent opacity-35',
                    !outside && !selected && 'border-[var(--border)] bg-white/[0.02] hover:border-accent/30',
                    selected && 'border-accent bg-accent/15',
                    today && !selected && 'ring-1 ring-accent/40',
                    count > 0 && !selected && markers.includes('programada') && 'border-violet-500/25 bg-violet-500/[0.05]',
                  )}
                >
                  <span className={cn('text-sm font-semibold', selected && 'text-accent')}>
                    {format(day, 'd')}
                  </span>
                  {markers.length > 0 && (
                    <span className='mt-1 flex items-center gap-0.5'>
                      {markers.map((marker) => (
                        <span
                          key={marker}
                          className={cn('h-1.5 w-1.5 rounded-full', FERIAS_DIA_MARKER_CLASS[marker])}
                        />
                      ))}
                    </span>
                  )}
                  {count > 0 && (
                    <span className='absolute right-1 top-1 rounded-full bg-white/[0.08] px-1 py-0.5 text-[8px] font-bold text-text-muted'>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <div className='glass-card p-4 md:p-5'>
          <h3 className='text-sm font-semibold uppercase tracking-wide text-text-primary'>
            {selectedDay
              ? format(selectedDay, "EEEE, dd 'de' MMMM", { locale: ptBR })
              : 'Selecione um dia'}
          </h3>
          {selectedEvents.length === 0 ? (
            <p className='mt-3 text-sm text-text-muted'>Nenhuma férias ou solicitação neste dia.</p>
          ) : (
            <ul className='mt-3 space-y-2'>
              {selectedEvents.map((evento) => {
                const meta = eventoDisplayMeta(evento)
                return (
                  <li key={evento.id} className='rounded-lg border border-border px-3 py-2'>
                    <div className='flex flex-wrap items-center justify-between gap-2'>
                      <button
                        type='button'
                        className='font-medium text-left hover:text-accent'
                        onClick={() => onSelectMember(evento.pjAgradaId)}
                      >
                        {evento.nomeCompleto}
                      </button>
                      <span className={cn('rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase', meta.className)}>
                        {meta.label}
                      </span>
                    </div>
                    <p className='mt-1 text-xs text-text-muted'>
                      {formatPtDay(new Date(evento.inicio))} → {formatPtDay(new Date(evento.fim))}
                      {evento.dias != null ? ` · ${evento.dias} dia(s)` : ''}
                    </p>
                    {evento.requestId && (
                      <Link
                        to={`/gestores/chamados/${evento.requestId}`}
                        className='mt-1 inline-block text-xs font-medium text-accent hover:underline'
                      >
                        Chamado {evento.protocol}
                      </Link>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      <GestorEquipeFeriasCalendarAside data={data} onSelectMember={onSelectMember} />
    </div>
  )
}
